<template>
  <section class="multi-positions" aria-labelledby="multi-position-heading" :aria-busy="saving || evaluating">
    <h2 id="multi-position-heading">Positionen erfassen</h2>
    <p v-for="error in schemaErrors" :key="error" class="multi-error" role="alert">{{ error }}</p>
    <p v-if="message" class="multi-message" role="status">{{ message }}</p>
    <div class="multi-table-scroll" tabindex="0" aria-label="Positionstabelle, horizontal scrollbar">
      <table @keydown="$emit('navigate', $event)">
        <thead>
          <tr>
            <th scope="col">Raum</th>
            <th scope="col" class="multi-amount-cell">Anzahl *</th>
            <th v-for="field in columns" :key="field.key" scope="col">
              {{ field.label }}{{ field.required ? ' *' : '' }}
              <small v-if="field.unit">{{ field.unit }}</small>
            </th>
            <th v-if="showPrices" scope="col">{{ priceLabel }}</th>
            <th scope="col" class="multi-remove-heading"><span class="multi-sr-only">Löschen</span></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, index) in rows" :key="row.key">
            <tr :data-multi-row="row.key" :class="{ 'multi-row-saved': row.savedId }">
              <td>
                <MultiPositionField v-slot="{ attributes }" :error="fieldError(row, 'room')" :error-id="errorId(row, 'room')">
                  <input v-bind="attributes" v-room-designation :value="row.room" maxlength="7" autocomplete="off"
                         :aria-label="`Raum Position ${index + 1}`" :disabled="locked(row)"
                         @input="change(row, 'room', $event.target.value)" @change="$emit('commit')">
                </MultiPositionField>
              </td>
              <td class="multi-amount-cell">
                <MultiPositionField v-slot="{ attributes }" :error="fieldError(row, 'amount')" :error-id="errorId(row, 'amount')">
                  <input v-bind="attributes" :value="row.amount" inputmode="numeric" autocomplete="off" class="multi-amount"
                         :aria-label="`Anzahl Position ${index + 1}`" :disabled="locked(row)"
                         @input="change(row, 'amount', $event.target.value)" @change="$emit('commit')">
                </MultiPositionField>
              </td>
              <td v-for="field in columns" :key="field.key">
                <span v-if="row.availability[field.key]?.visible === false" class="multi-inactive">–</span>
                <template v-else>
                  <MultiPositionField v-slot="{ attributes }" :error="fieldError(row, field.key)" :error-id="errorId(row, field.key)">
                    <select v-if="field.type === 'select'" v-bind="attributes" :value="row.fields[field.key]?.selectedIndex"
                            :aria-label="`${field.label} Position ${index + 1}`" :disabled="locked(row) || fieldLocked(row, field)"
                            @change="selectField(row, field, Number($event.target.value))">
                      <option v-for="(option, optionIndex) in field.options" :key="optionIndex" :value="optionIndex" :disabled="option.disabled">{{ option.label }}</option>
                    </select>
                    <input v-else-if="field.type === 'checkbox'" v-bind="attributes" type="checkbox" :checked="row.fields[field.key]?.checked"
                           :aria-label="`${field.label} Position ${index + 1}`" :disabled="locked(row) || fieldLocked(row, field)"
                           @change="change(row, field, $event.target.checked); $emit('commit')">
                    <input v-else v-bind="attributes" :value="row.fields[field.key]?.value" :inputmode="field.numeric ? 'decimal' : 'text'"
                           autocomplete="off" :placeholder="field.placeholder" :aria-label="`${field.label} Position ${index + 1}`"
                           :disabled="locked(row) || fieldLocked(row, field)" :title="fieldWarning(row, field)"
                           :class="{ 'multi-calculated-override': fieldWarning(row, field) }"
                           @input="change(row, field, $event.target.value)" @change="$emit('commit')">
                  </MultiPositionField>
                  <small v-if="fieldWarning(row, field)" class="multi-calculation-warning">{{ fieldWarning(row, field) }}</small>
                </template>
              </td>
              <td v-if="showPrices" class="multi-price">
                <span v-if="row.price == null">–</span>
                <span v-else-if="row.priceOnRequest">Preis auf Anfrage</span>
                <template v-else>
                  <div class="multi-price-base">
                    <span class="multi-price-amount">{{ formatPrice(row.price) }}</span> <span class="multi-price-tax">zzgl. MwSt.</span>
                  </div>
                  <div v-if="showSurcharge && priceForRow(row) != null" class="multi-price-with-tz">
                    <span class="multi-price-amount">{{ formatPrice(priceForRow(row)) }}</span> <span class="multi-price-tax">inkl. TZ, zzgl. MwSt.</span>
                  </div>
                </template>
              </td>
              <td class="multi-remove-cell">
                <button type="button" class="multi-remove" :aria-label="`Position ${index + 1} löschen`" :disabled="locked(row)"
                        @click="$emit('remove', row)"><span aria-hidden="true">×</span></button>
              </td>
            </tr>
            <tr v-if="row.generalErrors?.length || row.fieldErrors?.notes || row.oversizeMessage" class="multi-row-notices">
              <td :colspan="columnCount">
                <p v-for="error in row.generalErrors" :key="error" class="multi-error" role="alert">{{ rowContext(row, index) }}: {{ error }}</p>
                <p v-if="row.fieldErrors?.notes" class="multi-error" role="alert">{{ rowContext(row, index) }}: {{ row.fieldErrors.notes }}</p>
                <p v-if="row.oversizeMessage" class="multi-oversize-notice" role="note">{{ rowContext(row, index) }} – Achtung: {{ row.oversizeMessage }}</p>
              </td>
            </tr>
          </template>
        </tbody>
        <tfoot>
          <tr><td :colspan="columnCount">
            <button type="button" class="multi-add" aria-label="Neue Position hinzufügen" title="Neue Position hinzufügen"
                    :disabled="saving || readOnly" @click="$emit('add')"><span aria-hidden="true">+</span></button>
          </td></tr>
        </tfoot>
      </table>
    </div>
    <div class="multi-footer">
      <button v-if="uncertain" type="button" :disabled="saving || evaluating" @click="$emit('resolve')">Speicherstatus prüfen</button>
      <button v-if="finalizationPending" type="button" :disabled="!canFinalize" @click="$emit('finalize')">Wiederholen</button>
      <button type="button" class="multi-save" :disabled="!canSave" @click="$emit('save')">
        {{ saving ? 'Positionen werden gespeichert …' : 'Positionen in den Warenkorb speichern' }}
      </button>
    </div>
  </section>
</template>

<script>
import roomDesignation from '@/directives/roomDesignation';
import { formatPrice } from '@/utils/numberFormat';
import { getMultiPositionFieldWarning } from '@/configurator/multiPositionFields';
import MultiPositionField from './MultiPositionField.vue';

export default {
  components: { MultiPositionField },
  directives: { roomDesignation },
  props: {
    rows: { type: Array, required: true }, columns: { type: Array, required: true },
    schema: { type: Object, required: true }, schemaErrors: { type: Array, default: () => [] },
    saving: Boolean, evaluating: Boolean, readOnly: Boolean, showPrices: Boolean,
    canSave: Boolean, canFinalize: Boolean, finalizationPending: Boolean, uncertain: Boolean, message: { type: String, default: '' },
    priceForRow: { type: Function, required: true },
    showSurcharge: { type: Boolean, default: true },
    priceLabel: { type: String, default: 'Listenpreis' },
  },
  emits: ['add', 'remove', 'change', 'commit', 'navigate', 'save', 'resolve', 'finalize'],
  computed: {
    columnCount() { return this.columns.length + (this.showPrices ? 4 : 3); },
  },
  methods: {
    formatPrice,
    locked(row) { return this.saving || this.readOnly || !!row.savedId || ['saving', 'uncertain'].includes(row.status); },
    fieldLocked(row, field) { return field.readOnly || row.availability[field.key]?.disabled; },
    fieldError(row, key) { return row.fieldErrors?.[key] || ''; },
    errorId(row, key) { return `multi-error-${row.key}-${key.replace(':', '-')}`; },
    rowContext(row, index) { return row.room ? `Raum ${row.room}` : `Zeile ${index + 1}`; },
    change(row, field, value) { this.$emit('change', row, field, value); },
    selectField(row, field, selectedIndex) {
      this.change(row, field, { value: field.options[selectedIndex].value, selectedIndex });
      this.$emit('commit');
    },
    fieldWarning(row, field) { return getMultiPositionFieldWarning(row, field, this.schema); },
  },
};
</script>

<style scoped>
.multi-positions { margin: 16px 0; padding: 12px; border: 1px solid #dedede; border-radius: 6px; background: #fff; }
h2 { margin: 0; color: #555; font-size: 17px; }
.multi-table-scroll { overflow-x: auto; margin: 10px 0; border: 1px solid #e6e6e6; border-radius: 4px; }
table { width: 100%; border-collapse: collapse; font-size: 13px; }
th, td { padding: 6px; text-align: left; border-bottom: 1px solid #e8e8e8; vertical-align: top; }
thead th { background: #f6f6f6; color: #555; white-space: nowrap; }
th small { display: block; font-weight: normal; }
input:not([type="checkbox"]), select { box-sizing: border-box; width: 100%; min-width: 105px; padding: 7px; border: 1px solid #d5d5d5; border-radius: 3px; background: #fff; color: #444; font: inherit; }
input.multi-amount { min-width: 68px; width: 68px; }
.multi-amount-cell { width: 1%; white-space: nowrap; }
input:focus, select:focus, button:focus-visible { outline: 2px solid #a71930; outline-offset: 1px; }
input:disabled, select:disabled { background: #f5f5f5; color: #777; }
button { padding: 8px 12px; border: 1px solid #ddd; border-radius: 4px; background: #fafafa; color: #555; font: inherit; cursor: pointer; }
button:disabled { opacity: .5; cursor: default; }
.multi-footer { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
button.multi-save { background: #a71930; color: white; border-color: #a71930; }
.multi-remove-heading, .multi-remove-cell { width: 26px; min-width: 26px; }
button.multi-remove, button.multi-add { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; padding: 0; font-size: 21px; }
button.multi-remove { border-color: transparent; background: transparent; }
button.multi-add { color: #a71930; background: #fff; }
tfoot td { padding: 6px; border-bottom: 0; }
.multi-price { white-space: nowrap; vertical-align: middle; }
.multi-price-base, .multi-price-with-tz { display: flex; align-items: baseline; gap: 4px; font-weight: 600; }
.multi-price-amount { color: #e30613; }
.multi-price-base .multi-price-amount { font-size: 18px; }
.multi-price-with-tz { margin-top: 4px; font-size: 14px; }
.multi-price-tax { color: #777; font-size: 12px; }
.multi-row-saved { background: #f5faf5; }
.multi-error { color: #a71930; }
.multi-row-notices p { margin: 2px 0; font-size: 12px; }
.multi-message { padding: 8px; background: #f6f6f6; font-size: 13px; }
.multi-inactive { color: #aaa; }
.multi-calculation-warning { display: block; color: #925900; font-size: 11px; margin-top: 4px; }
input.multi-calculated-override:not(.input-error) { border-color: #bd8c36; }
.multi-sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
@media (max-width: 700px) { .multi-positions { padding: 8px; } .multi-footer button { width: 100%; } }
</style>
